import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  Search,
  Users,
  Briefcase,
  CheckSquare,
  Loader2,
  ArrowRight,
} from "lucide-react"
import { searchApi } from "@/lib/api"
import { formatPhone } from "@shared/utils/phone"
import { formatCurrency } from "@shared/utils/currency"
import { cn } from "@/lib/utils"

const DEBOUNCE_MS = 200

/**
 * Global search input with typeahead dropdown.
 * Searches contacts, deals, and tasks by name/email/phone/ABN/business name.
 * Cmd/Ctrl+K focuses the input from anywhere.
 */
export function GlobalSearch() {
  const [q, setQ] = useState("")
  const [debouncedQ, setDebouncedQ] = useState("")
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q])

  const results = useQuery({
    queryKey: ["search", debouncedQ],
    queryFn: () => searchApi.query(debouncedQ),
    enabled: debouncedQ.length >= 2,
    staleTime: 30_000,
  })

  // Cmd/Ctrl+K to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
      if (e.key === "Escape") {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener("mousedown", handler)
    return () => window.removeEventListener("mousedown", handler)
  }, [])

  const items: Array<{ href: string; label: React.ReactNode; type: string }> = []
  if (results.data && debouncedQ.length >= 2) {
    for (const c of results.data.contacts) {
      items.push({
        href: `/contacts/${c.id}`,
        type: "contact",
        label: <ContactRow hit={c} q={debouncedQ} />,
      })
    }
    for (const d of results.data.deals) {
      items.push({
        href: `/deals/${d.id}`,
        type: "deal",
        label: <DealRow hit={d} q={debouncedQ} />,
      })
    }
    for (const t of results.data.tasks) {
      items.push({
        href: t.contactId ? `/contacts/${t.contactId}` : "/tasks",
        type: "task",
        label: <TaskRow hit={t} q={debouncedQ} />,
      })
    }
  }

  const showDropdown = open && (debouncedQ.length >= 2 || q.length >= 2)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || items.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setFocusedIndex((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = items[focusedIndex]
      if (item) {
        navigate(item.href)
        setOpen(false)
        setQ("")
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full min-w-[220px] max-w-[520px] xl:max-w-[640px]"
    >
      <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
      <input
        ref={inputRef}
        type="search"
        placeholder="Search contacts, deals, tasks…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setFocusedIndex(0)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="border-input bg-rc-grey-50 hover:bg-white focus:bg-white focus:border-ring focus:ring-ring/20 h-10 w-full rounded-lg border pl-9 pr-3 text-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-4"
      />

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
          {results.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : !results.data ? (
            <div className="p-4 text-sm text-muted-foreground">Type at least 2 characters…</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No results for "<strong>{debouncedQ}</strong>"
            </div>
          ) : (
            <div>
              {results.data.contacts.length > 0 && (
                <Section title="Contacts" icon={Users} count={results.data.contacts.length}>
                  {results.data.contacts.map((c, i) => {
                    const idx = i
                    return (
                      <DropdownItem
                        key={c.id}
                        href={`/contacts/${c.id}`}
                        active={idx === focusedIndex}
                        onSelect={() => {
                          setOpen(false)
                          setQ("")
                        }}
                      >
                        <ContactRow hit={c} q={debouncedQ} />
                      </DropdownItem>
                    )
                  })}
                </Section>
              )}
              {results.data.deals.length > 0 && (
                <Section title="Deals" icon={Briefcase} count={results.data.deals.length}>
                  {results.data.deals.map((d, i) => {
                    const idx = results.data!.contacts.length + i
                    return (
                      <DropdownItem
                        key={d.id}
                        href={`/deals/${d.id}`}
                        active={idx === focusedIndex}
                        onSelect={() => {
                          setOpen(false)
                          setQ("")
                        }}
                      >
                        <DealRow hit={d} q={debouncedQ} />
                      </DropdownItem>
                    )
                  })}
                </Section>
              )}
              {results.data.tasks.length > 0 && (
                <Section title="Tasks" icon={CheckSquare} count={results.data.tasks.length}>
                  {results.data.tasks.map((t, i) => {
                    const idx =
                      results.data!.contacts.length + results.data!.deals.length + i
                    return (
                      <DropdownItem
                        key={t.id}
                        href={t.contactId ? `/contacts/${t.contactId}` : "/tasks"}
                        active={idx === focusedIndex}
                        onSelect={() => {
                          setOpen(false)
                          setQ("")
                        }}
                      >
                        <TaskRow hit={t} q={debouncedQ} />
                      </DropdownItem>
                    )
                  })}
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string
  icon: typeof Search
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-2 border-b border-border bg-rc-grey-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
        <span className="text-muted-foreground/70">({count})</span>
      </div>
      {children}
    </div>
  )
}

function DropdownItem({
  href,
  active,
  onSelect,
  children,
}: {
  href: string
  active: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <Link
      to={href}
      onClick={onSelect}
      className={cn(
        "flex items-center gap-3 border-b border-border px-3 py-2 text-sm hover:bg-rc-grey-50 last:border-b-0",
        active && "bg-rc-blue-50"
      )}
    >
      <div className="flex-1 min-w-0">{children}</div>
      <ArrowRight className="text-muted-foreground h-3 w-3 shrink-0" />
    </Link>
  )
}

function Highlight({ text, q }: { text: string | null | undefined; q: string }) {
  if (!text) return <>—</>
  if (!q) return <>{text}</>
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-100 text-amber-900 rounded-sm px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  )
}

function ContactRow({ hit, q }: { hit: NonNullable<Awaited<ReturnType<typeof searchApi.query>>>["contacts"][number]; q: string }) {
  const name = `${hit.firstName} ${hit.lastName}`.trim()
  return (
    <div>
      <div className="truncate font-medium text-foreground">
        <Highlight text={name} q={q} />
        {hit.businessName && (
          <span className="text-muted-foreground text-xs">
            {" "}
            · <Highlight text={hit.businessName} q={q} />
          </span>
        )}
      </div>
      <div className="text-muted-foreground truncate text-xs">
        {hit.email && (
          <>
            <Highlight text={hit.email} q={q} />
            {(hit.mobile || hit.abn) && " · "}
          </>
        )}
        {hit.mobile && (
          <>
            <Highlight text={formatPhone(hit.mobile)} q={q} />
            {hit.abn && " · "}
          </>
        )}
        {hit.abn && <>ABN <Highlight text={hit.abn} q={q} /></>}
      </div>
    </div>
  )
}

function DealRow({ hit, q }: { hit: NonNullable<Awaited<ReturnType<typeof searchApi.query>>>["deals"][number]; q: string }) {
  const contactName =
    hit.contactBusinessName ??
    `${hit.contactFirstName ?? ""} ${hit.contactLastName ?? ""}`.trim() ??
    "—"
  const amount = hit.naf ?? hit.loanAmount
  return (
    <div>
      <div className="truncate font-medium text-foreground">
        <Highlight text={hit.dealName ?? "Untitled deal"} q={q} />
      </div>
      <div className="text-muted-foreground truncate text-xs">
        {contactName}
        {hit.lender && ` · ${hit.lender}`}
        {amount ? ` · ${formatCurrency(amount)}` : ""}
        {` · ${hit.status.replace(/_/g, " ")}`}
      </div>
    </div>
  )
}

function TaskRow({ hit, q }: { hit: NonNullable<Awaited<ReturnType<typeof searchApi.query>>>["tasks"][number]; q: string }) {
  const contactName =
    hit.contactBusinessName ??
    (hit.contactFirstName
      ? `${hit.contactFirstName} ${hit.contactLastName ?? ""}`.trim()
      : null)
  return (
    <div>
      <div className="truncate font-medium text-foreground">
        <Highlight text={hit.title} q={q} />
      </div>
      <div className="text-muted-foreground truncate text-xs">
        {hit.status} · {hit.priority}
        {contactName && ` · ${contactName}`}
      </div>
    </div>
  )
}
